import { useEffect, useRef, useState } from "react";
import { speechSupported, speak, stopSpeaking } from "../lib/speech.js";

const TYPE_LABEL = {
  combined: "ATIS",
  arr: "Arrival ATIS",
  dep: "Departure ATIS",
  metar: "Latest observation (METAR)",
};

// Printed ATIS text with a "read aloud" button — the spoken broadcast, in print.
export default function AtisText({ icao }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [speaking, setSpeaking] = useState(false);
  const canSpeak = speechSupported();

  useEffect(() => {
    if (!icao) return;
    const controller = new AbortController();
    setLoading(true);
    setData(null);
    fetch(`/api/atis/${icao}`, { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [icao]);

  // Stop any speech if the panel/airport changes.
  useEffect(() => {
    setSpeaking(false);
    stopSpeaking();
    return () => stopSpeaking();
  }, [icao]);

  const blocks = data?.atis || [];

  function toggleSpeak() {
    if (speaking) {
      stopSpeaking();
      setSpeaking(false);
      return;
    }
    const text = blocks.map((b) => b.text).join(". ");
    if (!text) return;
    const u = speak(text);
    if (!u) return;
    setSpeaking(true);
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
  }

  if (loading) return <p className="muted small">Fetching ATIS…</p>;
  if (!blocks.length) return null;

  return (
    <div className="atis">
      <div className="atis-head">
        <span className="atis-title">
          🖨️ Printed ATIS{data?.source ? ` · ${data.source}` : ""}
        </span>
        {canSpeak && (
          <button
            type="button"
            className={speaking ? "atis-speak speaking" : "atis-speak"}
            onClick={toggleSpeak}
            aria-pressed={speaking}
            aria-label={speaking ? "Stop reading ATIS aloud" : "Read ATIS aloud"}
          >
            {speaking ? "⏹ Stop" : "🔊 Read aloud"}
          </button>
        )}
      </div>
      {blocks.map((b, i) => (
        <div key={i} className="atis-block">
          {b.type !== "metar" ? (
            <div className="atis-type">
              {TYPE_LABEL[b.type] || "ATIS"}
              {b.code ? ` · Information ${b.code}` : ""}
            </div>
          ) : (
            <div className="atis-type">{TYPE_LABEL.metar}</div>
          )}
          <pre className="atis-body">{b.text}</pre>
        </div>
      ))}
    </div>
  );
}
